/**
 * Typed mirror of `tokens.css`.
 *
 * TS and TSX reference tokens through this object, never as strings: a typo in
 * `var(--c-txet-primary)` is silent at runtime and invisible in review, while
 * `token.text.primary` is a compile error the moment it is wrong. Values are
 * `var(...)` references, so a style object stays a live CSS reference and
 * still resolves through the cascade.
 *
 * Only the PUBLIC layers are mirrored. The raw ramp (`--c-gray-70`,
 * `--c-purple-60`, …) is deliberately absent: it is private to
 * `packages/design-system/src/tokens/` (DESIGN-DECISION.md §7.1). If you need
 * a colour that is not here, the semantic layer is missing a role — add the
 * role, do not reach past it.
 *
 * `tokens.test.ts` asserts every name below is declared in `tokens.css`.
 */

/** A CSS custom-property reference. Every token value is one of these. */
export type CssVar = `var(--${string})`;

const cssVar = <N extends string>(name: N): `var(--${N})` => `var(--${name})` as `var(--${N})`;

/* -------------------------------------------------------------------------- */
/* Semantic scales — the vocabularies a component prop may be typed against.  */
/* -------------------------------------------------------------------------- */

/** §6.2 — colour classifies or it is grey. These are the only classifications. */
export type Classification =
  | 'standard'
  | 'agentic'
  | 'gate'
  | 'loop'
  | 'artifact'
  | 'protocol'
  | 'skill'
  | 'unclassified';

/** The four roles every classification provides. */
export type ClassificationRole = 'base' | 'subtle' | 'border' | 'inverse';

export type SurfaceRole =
  | 'background'
  | 'layer01'
  | 'layer02'
  | 'layer03'
  | 'layerAccent'
  | 'shell'
  | 'shellHover'
  | 'shellLine'
  | 'shellSelected'
  | 'shellAccent'
  | 'highlightHover';

export type TextRole =
  | 'primary'
  | 'secondary'
  | 'helper'
  | 'onColor'
  | 'inverse'
  | 'error'
  | 'shell'
  | 'shellDim';

export type BorderRole = 'subtle' | 'control' | 'strong' | 'inverse' | 'interactive';

export type SupportStatus = 'info' | 'success' | 'warning' | 'error';

/** §17 — every interactive component implements all five. */
export type InteractionState = 'rest' | 'hover' | 'active' | 'selected' | 'disabled';

/** The six typed dependency edges the composition canvas draws. */
export type EdgeType =
  | 'precedes'
  | 'dependsOn'
  | 'loopsTo'
  | 'produces'
  | 'performs'
  | 'governedByGate';

/** Ontology node kinds that carry a classification rail. */
export type NodeKind =
  | 'phase'
  | 'workPractice'
  | 'stage'
  | 'activity'
  | 'role'
  | 'agent'
  | 'agentSkill'
  | 'mcpServer'
  | 'tool'
  | 'asset'
  | 'deliverable'
  | 'gate'
  | 'lens'
  | 'ceremony';

export type MethodVariant = 'standard' | 'agentic';
export type StageState = 'complete' | 'current' | 'pending';
export type GateVerdict = 'pass' | 'fail' | 'pending' | 'waived';
export type GateModel = 'automated' | 'human';
export type AutomationLevel = 'automated' | 'assisted' | 'manual';
export type DeltaDirection = 'improve' | 'regress' | 'neutral';
export type LensState = 'applied' | 'off';
export type LifecycleStatus = 'draft' | 'inReview' | 'published' | 'blocked' | 'locked';
export type TailoringDecision = 'include' | 'adapt' | 'skip';
export type FindingSeverity = 'blocking' | 'warning' | 'info' | 'pass';

/** §8.2 — one of these 18 recipes, never a size mixed with an arbitrary weight. */
export type TypeStyleName =
  | 'caption01'
  | 'label01'
  | 'labelCaps'
  | 'helper01'
  | 'legal01'
  | 'bodyCompact'
  | 'body01'
  | 'body02'
  | 'heading01'
  | 'heading02'
  | 'heading03'
  | 'heading04'
  | 'heading05'
  | 'heading06'
  | 'display01'
  | 'code01'
  | 'code02'
  | 'code03';

export type SpaceStep =
  | 'sp01'
  | 'sp02'
  | 'sp03'
  | 'sp04'
  | 'sp05'
  | 'sp06'
  | 'sp07'
  | 'sp08'
  | 'sp09'
  | 'sp10'
  | 'sp11'
  | 'sp12'
  | 'sp13';

export type ZLayer =
  | 'edge'
  | 'node'
  | 'canvasHud'
  | 'drag'
  | 'sticky'
  | 'shell'
  | 'menu'
  | 'overlay'
  | 'modal'
  | 'toast'
  | 'tooltip';

export type BreakpointName = 'sm' | 'md' | 'lg' | 'xlg' | 'max';

/** §14 `DataTable` column presets. The preset → token mapping lives in the table. */
export type ColumnPreset =
  | 'id'
  | 'identity'
  | 'prose'
  | 'actors'
  | 'actorsCompact'
  | 'output'
  | 'matrix'
  | 'meter'
  | 'flex';

/* -------------------------------------------------------------------------- */
/* The mirror                                                                 */
/* -------------------------------------------------------------------------- */

export const token = {
  surface: {
    background: cssVar('c-background'),
    layer01: cssVar('c-layer-01'),
    layer02: cssVar('c-layer-02'),
    layer03: cssVar('c-layer-03'),
    layerAccent: cssVar('c-layer-accent'),
    shell: cssVar('c-shell'),
    shellHover: cssVar('c-shell-hover'),
    shellLine: cssVar('c-shell-line'),
    shellSelected: cssVar('c-shell-selected'),
    shellAccent: cssVar('c-shell-accent'),
    highlightHover: cssVar('c-highlight-hover'),
  },

  field: {
    onBackground: cssVar('c-field-01'),
    onLayer01: cssVar('c-field-02'),
  },

  text: {
    primary: cssVar('c-text-primary'),
    secondary: cssVar('c-text-secondary'),
    helper: cssVar('c-text-helper'),
    onColor: cssVar('c-text-on-color'),
    inverse: cssVar('c-text-inverse'),
    error: cssVar('c-text-error'),
    shell: cssVar('c-shell-text'),
    shellDim: cssVar('c-shell-text-dim'),
    link: cssVar('c-link'),
    linkVisited: cssVar('c-link-visited'),
  },

  border: {
    subtle: cssVar('c-border-subtle'),
    control: cssVar('c-border-control'),
    strong: cssVar('c-border-strong'),
    inverse: cssVar('c-border-inverse'),
    interactive: cssVar('c-border-interactive'),
  },

  support: {
    info: cssVar('c-support-info'),
    success: cssVar('c-support-success'),
    warning: cssVar('c-support-warning'),
    error: cssVar('c-support-error'),
  },

  classification: {
    standard: {
      base: cssVar('c-standard'),
      subtle: cssVar('c-standard-subtle'),
      border: cssVar('c-standard-border'),
      inverse: cssVar('c-standard-inverse'),
    },
    agentic: {
      base: cssVar('c-agentic'),
      subtle: cssVar('c-agentic-subtle'),
      border: cssVar('c-agentic-border'),
      inverse: cssVar('c-agentic-inverse'),
    },
    gate: {
      base: cssVar('c-gate'),
      subtle: cssVar('c-gate-subtle'),
      border: cssVar('c-gate-border'),
      inverse: cssVar('c-gate-inverse'),
    },
    loop: {
      base: cssVar('c-loop'),
      subtle: cssVar('c-loop-subtle'),
      border: cssVar('c-loop-border'),
      inverse: cssVar('c-loop-inverse'),
    },
    artifact: {
      base: cssVar('c-artifact'),
      subtle: cssVar('c-artifact-subtle'),
      border: cssVar('c-artifact-border'),
      inverse: cssVar('c-artifact-inverse'),
    },
    protocol: {
      base: cssVar('c-protocol'),
      subtle: cssVar('c-protocol-subtle'),
      border: cssVar('c-protocol-border'),
      inverse: cssVar('c-protocol-inverse'),
    },
    skill: {
      base: cssVar('c-skill'),
      subtle: cssVar('c-skill-subtle'),
      border: cssVar('c-skill-border'),
      inverse: cssVar('c-skill-inverse'),
    },
    unclassified: {
      base: cssVar('c-unclassified'),
      subtle: cssVar('c-unclassified-subtle'),
      border: cssVar('c-unclassified-border'),
      inverse: cssVar('c-unclassified-inverse'),
    },
  },

  interactive: {
    base: cssVar('c-interactive'),
    buttonPrimary: cssVar('c-button-primary'),
    buttonPrimaryHover: cssVar('c-button-primary-hover'),
    buttonPrimaryActive: cssVar('c-button-primary-active'),
    buttonSecondary: cssVar('c-button-secondary'),
    buttonSecondaryHover: cssVar('c-button-secondary-hover'),
    buttonDanger: cssVar('c-button-danger'),
    selectedUi: cssVar('c-selected-ui'),
    selectedLight: cssVar('c-selected-light'),
  },

  focus: {
    ring: cssVar('c-focus'),
    ringInset: cssVar('c-focus-inset'),
    width: cssVar('c-focus-width'),
    offset: cssVar('c-focus-offset'),
    offsetInset: cssVar('c-focus-offset-inset'),
    ringInner: cssVar('c-focus-ring-inner'),
  },

  state: {
    hover: cssVar('c-state-hover'),
    hoverInverse: cssVar('c-state-hover-inverse'),
    active: cssVar('c-state-active'),
    activeInverse: cssVar('c-state-active-inverse'),
    selected: cssVar('c-state-selected'),
    selectedBorder: cssVar('c-state-selected-border'),
    disabledBg: cssVar('c-state-disabled-bg'),
    disabledText: cssVar('c-state-disabled-text'),
    disabledBorder: cssVar('c-state-disabled-border'),
    dropTarget: cssVar('c-state-drop-target'),
    dropTargetArmed: cssVar('c-state-drop-target-armed'),
  },

  elevation: {
    none: cssVar('c-shadow-00'),
    popover: cssVar('c-shadow-01'),
    modal: cssVar('c-shadow-02'),
    menu: cssVar('c-shadow-menu'),
    drag: cssVar('c-shadow-drag'),
    ringInsetWidth: cssVar('c-ring-inset-width'),
  },

  overlay: {
    scrim: cssVar('c-overlay'),
  },

  type: {
    fontSans: cssVar('c-font-sans'),
    fontMono: cssVar('c-font-mono'),
    size: {
      caption01: cssVar('c-fs-caption-01'),
      label01: cssVar('c-fs-label-01'),
      helper01: cssVar('c-fs-helper-01'),
      legal01: cssVar('c-fs-legal-01'),
      bodyCompact: cssVar('c-fs-body-compact'),
      body01: cssVar('c-fs-body-01'),
      body02: cssVar('c-fs-body-02'),
      heading01: cssVar('c-fs-heading-01'),
      heading02: cssVar('c-fs-heading-02'),
      heading03: cssVar('c-fs-heading-03'),
      heading04: cssVar('c-fs-heading-04'),
      heading05: cssVar('c-fs-heading-05'),
      heading06: cssVar('c-fs-heading-06'),
      display01: cssVar('c-fs-display-01'),
      code01: cssVar('c-fs-code-01'),
      code02: cssVar('c-fs-code-02'),
      code03: cssVar('c-fs-code-03'),
    },
    lineHeight: {
      caption01: cssVar('c-lh-caption-01'),
      label01: cssVar('c-lh-label-01'),
      helper01: cssVar('c-lh-helper-01'),
      legal01: cssVar('c-lh-legal-01'),
      bodyCompact: cssVar('c-lh-body-compact'),
      body01: cssVar('c-lh-body-01'),
      body02: cssVar('c-lh-body-02'),
      heading01: cssVar('c-lh-heading-01'),
      heading02: cssVar('c-lh-heading-02'),
      heading03: cssVar('c-lh-heading-03'),
      heading04: cssVar('c-lh-heading-04'),
      heading05: cssVar('c-lh-heading-05'),
      heading06: cssVar('c-lh-heading-06'),
      display01: cssVar('c-lh-display-01'),
      code01: cssVar('c-lh-code-01'),
      code02: cssVar('c-lh-code-02'),
      code03: cssVar('c-lh-code-03'),
    },
    weight: {
      light: cssVar('c-fw-light'),
      regular: cssVar('c-fw-regular'),
      medium: cssVar('c-fw-medium'),
      semibold: cssVar('c-fw-semibold'),
    },
    tracking: {
      tight: cssVar('c-ls-tight'),
      normal: cssVar('c-ls-normal'),
      label: cssVar('c-ls-label'),
      caps: cssVar('c-ls-caps'),
      capsWide: cssVar('c-ls-caps-wide'),
      heading: cssVar('c-ls-heading'),
    },
    measure: {
      narrow: cssVar('c-measure-narrow'),
      body: cssVar('c-measure-body'),
    },
  },

  space: {
    sp01: cssVar('c-sp-01'),
    sp02: cssVar('c-sp-02'),
    sp03: cssVar('c-sp-03'),
    sp04: cssVar('c-sp-04'),
    sp05: cssVar('c-sp-05'),
    sp06: cssVar('c-sp-06'),
    sp07: cssVar('c-sp-07'),
    sp08: cssVar('c-sp-08'),
    sp09: cssVar('c-sp-09'),
    sp10: cssVar('c-sp-10'),
    sp11: cssVar('c-sp-11'),
    sp12: cssVar('c-sp-12'),
    sp13: cssVar('c-sp-13'),
    gutter: cssVar('c-gutter'),
    gutterHalf: cssVar('c-gutter-half'),
  },

  radius: {
    none: cssVar('c-radius-00'),
    r01: cssVar('c-radius-01'),
    r02: cssVar('c-radius-02'),
    max: cssVar('c-radius-max'),
  },

  line: {
    hairline: cssVar('c-hairline'),
    borderWidth: cssVar('c-border-width'),
    topStrong: cssVar('c-border-top-strong'),
    accent: cssVar('c-border-accent'),
  },

  motion: {
    duration: {
      fast01: cssVar('c-dur-fast-01'),
      fast02: cssVar('c-dur-fast-02'),
      moderate01: cssVar('c-dur-moderate-01'),
      moderate02: cssVar('c-dur-moderate-02'),
      slow01: cssVar('c-dur-slow-01'),
      slow02: cssVar('c-dur-slow-02'),
      ambient: cssVar('c-dur-ambient'),
    },
    easing: {
      standard: cssVar('c-ease-standard'),
      entrance: cssVar('c-ease-entrance'),
      exit: cssVar('c-ease-exit'),
      linear: cssVar('c-ease-linear'),
    },
  },

  layout: {
    shellHeaderH: cssVar('c-shell-header-h'),
    shellRailW: cssVar('c-shell-rail-w'),
    subheadH: cssVar('c-subhead-h'),
    toolbarH: cssVar('c-toolbar-h'),
    statusbarH: cssVar('c-statusbar-h'),
    rowSm: cssVar('c-row-sm'),
    rowMd: cssVar('c-row-md'),
    rowLg: cssVar('c-row-lg'),
    rowXl: cssVar('c-row-xl'),
    panelInspector: cssVar('c-panel-inspector'),
    panelPalette: cssVar('c-panel-palette'),
    panelSide: cssVar('c-panel-side'),
    pageMax: cssVar('c-page-max'),
    contentPad: cssVar('c-content-pad'),
    drawerW: cssVar('c-drawer-w'),
    adminFormMax: cssVar('c-admin-form-max'),
    editorMinH: cssVar('c-editor-min-h'),
    treeIndent: cssVar('c-tree-indent'),
  },

  metric: {
    icon16: cssVar('c-icon-16'),
    icon20: cssVar('c-icon-20'),
    swatch: cssVar('c-swatch'),
    avatar: cssVar('c-avatar'),
    kbdH: cssVar('c-kbd-h'),
    meterH: cssVar('c-meter-h'),
    barH: cssVar('c-bar-h'),
    node: cssVar('c-node'),
    phaseBandH: cssVar('c-phaseband-h'),
    stageCardH: cssVar('c-stage-card-h'),
    loopH: cssVar('c-loop-h'),
    gateCellH: cssVar('c-gatecell-h'),
    statH: cssVar('c-stat-h'),
    metaBandH: cssVar('c-metaband-h'),
    drillHeadH: cssVar('c-drill-head-h'),
    canvasGrid: cssVar('c-canvas-grid'),
    cardNodeW: cssVar('c-cardnode-w'),
    cardNodeH: cssVar('c-cardnode-h'),
    laneW: cssVar('c-lane-w'),
    stripH: cssVar('c-strip-h'),
    execBandH: cssVar('c-execband-h'),
    modalW: cssVar('c-modal-w'),
    skeletonH: cssVar('c-skeleton-h'),
  },

  column: {
    id: cssVar('c-col-id'),
    identity: cssVar('c-col-stage'),
    prose: cssVar('c-col-desc'),
    actors: cssVar('c-col-role'),
    actorsCompact: cssVar('c-col-who'),
    output: cssVar('c-col-out'),
    matrix: cssVar('c-col-lens'),
    meter: cssVar('c-col-auto'),
  },

  z: {
    edge: cssVar('c-z-edge'),
    node: cssVar('c-z-node'),
    canvasHud: cssVar('c-z-canvas-hud'),
    drag: cssVar('c-z-drag'),
    sticky: cssVar('c-z-sticky'),
    shell: cssVar('c-z-shell'),
    menu: cssVar('c-z-menu'),
    overlay: cssVar('c-z-overlay'),
    modal: cssVar('c-z-modal'),
    toast: cssVar('c-z-toast'),
    tooltip: cssVar('c-z-tooltip'),
  },

  breakpoint: {
    sm: cssVar('c-bp-sm'),
    md: cssVar('c-bp-md'),
    lg: cssVar('c-bp-lg'),
    xlg: cssVar('c-bp-xlg'),
    max: cssVar('c-bp-max'),
  },

  /* Domain semantics — a surface asks for the meaning it renders, not a hue. */
  domain: {
    variant: {
      standard: cssVar('c-variant-standard'),
      agentic: cssVar('c-variant-agentic'),
    },
    kind: {
      slot: cssVar('c-kind'),
      phase: cssVar('c-kind-phase'),
      workPractice: cssVar('c-kind-work-practice'),
      stage: cssVar('c-kind-stage'),
      activity: cssVar('c-kind-activity'),
      role: cssVar('c-kind-role'),
      agent: cssVar('c-kind-agent'),
      agentSkill: cssVar('c-kind-agent-skill'),
      mcpServer: cssVar('c-kind-mcp-server'),
      tool: cssVar('c-kind-tool'),
      asset: cssVar('c-kind-asset'),
      deliverable: cssVar('c-kind-deliverable'),
      gate: cssVar('c-kind-gate'),
      lens: cssVar('c-kind-lens'),
      ceremony: cssVar('c-kind-ceremony'),
    },
    edge: {
      precedes: cssVar('c-edge-precedes'),
      dependsOn: cssVar('c-edge-depends-on'),
      loopsTo: cssVar('c-edge-loops-to'),
      produces: cssVar('c-edge-produces'),
      performs: cssVar('c-edge-performs'),
      governedByGate: cssVar('c-edge-governed-by-gate'),
    },
    stage: {
      rail: cssVar('c-stage-rail'),
      railAgentic: cssVar('c-stage-rail-agentic'),
      surface: cssVar('c-stage-surface'),
      border: cssVar('c-stage-border'),
      completeFill: cssVar('c-stage-complete-fill'),
      currentFill: cssVar('c-stage-current-fill'),
      pendingFill: cssVar('c-stage-pending-fill'),
      axis: cssVar('c-stage-axis'),
      metaSurface: cssVar('c-stage-meta-surface'),
      metaBorder: cssVar('c-stage-meta-border'),
    },
    phaseBand: {
      surface: cssVar('c-phase-band-surface'),
      text: cssVar('c-phase-band-text'),
      line: cssVar('c-phase-band-line'),
    },
    gate: {
      surface: cssVar('c-gate-surface'),
      boundary: cssVar('c-gate-boundary'),
      passText: cssVar('c-gate-pass-text'),
      passFill: cssVar('c-gate-pass-fill'),
      failText: cssVar('c-gate-fail-text'),
      failFill: cssVar('c-gate-fail-fill'),
      pendingText: cssVar('c-gate-pending-text'),
      pendingFill: cssVar('c-gate-pending-fill'),
      waivedText: cssVar('c-gate-waived-text'),
      waivedFill: cssVar('c-gate-waived-fill'),
      automatedText: cssVar('c-gate-automated-text'),
      automatedFill: cssVar('c-gate-automated-fill'),
      humanText: cssVar('c-gate-human-text'),
      humanFill: cssVar('c-gate-human-fill'),
    },
    automation: {
      automated: cssVar('c-automation-automated'),
      assisted: cssVar('c-automation-assisted'),
      manual: cssVar('c-automation-manual'),
      track: cssVar('c-automation-track'),
    },
    delta: {
      improve: cssVar('c-delta-improve'),
      regress: cssVar('c-delta-regress'),
      neutral: cssVar('c-delta-neutral'),
      figure: cssVar('c-delta-figure'),
      figureCaption: cssVar('c-delta-figure-caption'),
    },
    lens: {
      appliedFill: cssVar('c-lens-applied-fill'),
      appliedText: cssVar('c-lens-applied-text'),
      appliedBorder: cssVar('c-lens-applied-border'),
      offFill: cssVar('c-lens-off-fill'),
      offText: cssVar('c-lens-off-text'),
      offBorder: cssVar('c-lens-off-border'),
    },
    status: {
      draftFill: cssVar('c-status-draft-fill'),
      draftText: cssVar('c-status-draft-text'),
      inReviewFill: cssVar('c-status-in-review-fill'),
      inReviewText: cssVar('c-status-in-review-text'),
      publishedFill: cssVar('c-status-published-fill'),
      publishedText: cssVar('c-status-published-text'),
      blockedFill: cssVar('c-status-blocked-fill'),
      blockedText: cssVar('c-status-blocked-text'),
      lockedFill: cssVar('c-status-locked-fill'),
      lockedText: cssVar('c-status-locked-text'),
    },
    decision: {
      includeFill: cssVar('c-decision-include-fill'),
      includeText: cssVar('c-decision-include-text'),
      adaptFill: cssVar('c-decision-adapt-fill'),
      adaptText: cssVar('c-decision-adapt-text'),
      skipFill: cssVar('c-decision-skip-fill'),
      skipText: cssVar('c-decision-skip-text'),
    },
    finding: {
      blocking: cssVar('c-finding-blocking'),
      blockingText: cssVar('c-finding-blocking-text'),
      warning: cssVar('c-finding-warning'),
      warningText: cssVar('c-finding-warning-text'),
      info: cssVar('c-finding-info'),
      infoText: cssVar('c-finding-info-text'),
      pass: cssVar('c-finding-pass'),
      passText: cssVar('c-finding-pass-text'),
    },
    assist: {
      surface: cssVar('c-assist-surface'),
      border: cssVar('c-assist-border'),
      text: cssVar('c-assist-text'),
      rail: cssVar('c-assist-rail'),
    },
  },
} as const;

/**
 * §8.2 recipes. A text style is a row of the table — size, line-height, weight
 * and tracking together. Spreading one of these into a style object is the only
 * sanctioned way to set type, because mixing a size with an arbitrary weight is
 * what makes a Carbon layout stop looking like one.
 */
export const typeStyle = {
  caption01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.caption01,
    lineHeight: token.type.lineHeight.caption01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  label01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.label01,
    lineHeight: token.type.lineHeight.label01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  /** The universal zone label. Uppercase, on every surface. */
  labelCaps: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.label01,
    lineHeight: token.type.lineHeight.label01,
    fontWeight: token.type.weight.semibold,
    letterSpacing: token.type.tracking.caps,
    textTransform: 'uppercase',
  },
  helper01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.helper01,
    lineHeight: token.type.lineHeight.helper01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  legal01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.legal01,
    lineHeight: token.type.lineHeight.legal01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  bodyCompact: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.bodyCompact,
    lineHeight: token.type.lineHeight.bodyCompact,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  body01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.body01,
    lineHeight: token.type.lineHeight.body01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  body02: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.body02,
    lineHeight: token.type.lineHeight.body02,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  heading01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading01,
    lineHeight: token.type.lineHeight.heading01,
    fontWeight: token.type.weight.semibold,
    letterSpacing: token.type.tracking.normal,
  },
  heading02: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading02,
    lineHeight: token.type.lineHeight.heading02,
    fontWeight: token.type.weight.semibold,
    letterSpacing: token.type.tracking.normal,
  },
  heading03: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading03,
    lineHeight: token.type.lineHeight.heading03,
    fontWeight: token.type.weight.semibold,
    letterSpacing: token.type.tracking.tight,
  },
  heading04: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading04,
    lineHeight: token.type.lineHeight.heading04,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.tight,
  },
  heading05: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading05,
    lineHeight: token.type.lineHeight.heading05,
    fontWeight: token.type.weight.light,
    letterSpacing: token.type.tracking.tight,
  },
  heading06: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.heading06,
    lineHeight: token.type.lineHeight.heading06,
    fontWeight: token.type.weight.light,
    letterSpacing: token.type.tracking.tight,
  },
  /** Licensed for exactly one use: the cycle-time delta on the Compare band. */
  display01: {
    fontFamily: token.type.fontSans,
    fontSize: token.type.size.display01,
    lineHeight: token.type.lineHeight.display01,
    fontWeight: token.type.weight.light,
    letterSpacing: token.type.tracking.tight,
  },
  code01: {
    fontFamily: token.type.fontMono,
    fontSize: token.type.size.code01,
    lineHeight: token.type.lineHeight.code01,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  code02: {
    fontFamily: token.type.fontMono,
    fontSize: token.type.size.code02,
    lineHeight: token.type.lineHeight.code02,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
  code03: {
    fontFamily: token.type.fontMono,
    fontSize: token.type.size.code03,
    lineHeight: token.type.lineHeight.code03,
    fontWeight: token.type.weight.regular,
    letterSpacing: token.type.tracking.normal,
  },
} as const satisfies Record<TypeStyleName, Record<string, string>>;

/** Absolute path of the stylesheet these names mirror, for the token test. */
export const TOKENS_CSS_FILENAME = 'tokens.css';

/**
 * Resolve a classification to one of its four roles. Component code takes a
 * `Classification` prop and calls this — it never builds a token name by
 * string concatenation, which would defeat the type check.
 */
export const classificationToken = (
  classification: Classification,
  role: ClassificationRole = 'base',
): CssVar => token.classification[classification][role];

/** Resolve an ontology node kind to its classification rail colour. */
export const nodeKindToken = (kind: NodeKind): CssVar => token.domain.kind[kind];

/** Resolve a typed dependency edge to its stroke colour. */
export const edgeToken = (edge: EdgeType): CssVar => token.domain.edge[edge];

/** Resolve a `DataTable` column preset to its width token. `flex` has none. */
export const columnToken = (preset: ColumnPreset): CssVar | undefined =>
  preset === 'flex' ? undefined : token.column[preset];

type TokenTree = { readonly [key: string]: string | TokenTree };

const collect = (node: TokenTree, out: Set<string>): void => {
  for (const value of Object.values(node)) {
    if (typeof value === 'string') {
      const match = /^var\(--([a-z0-9-]+)\)$/.exec(value);
      if (match?.[1] !== undefined) out.add(match[1]);
    } else {
      collect(value, out);
    }
  }
};

/**
 * Every custom-property NAME (without the `--`) reachable from this mirror.
 * Used by `tokens.test.ts` to prove the mirror and the stylesheet agree, and
 * usable by a token-gallery dev surface.
 */
export const tokenVarNames = (): readonly string[] => {
  const names = new Set<string>();
  collect(token as unknown as TokenTree, names);
  return [...names].sort();
};
